export default class common {
  constructor() {
    this.setScrollbarFunc();
    this.smoothScroll();
    this.scrollAnimation();
    this.keyVisualReveal();
    this.improveAccessibility();
    this.initPrint();
  }

  // keyVisual のロード演出。初期状態（body opacity0 / 画像 scale1.1+blur+brightness）が
  // 描画されてから is-loaded を付与して CSS トランジションを発火させる。
  // #keyVisual を持つページ（画像ありKV）のみ対象。pageTtl 単体ページは対象外。
  keyVisualReveal() {
    const keyVisual = document.getElementById('keyVisual');
    if (!keyVisual) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        keyVisual.classList.add('is-loaded');
      });
    });
  }

  // commonFunc(TOPページの関数)
  setScrollbarFunc() {
    document.documentElement.style.setProperty('--scrollbar', `0px`);

    const setScrollbarWidth = () => {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar', `${scrollbarWidth}px`);
    };

    setScrollbarWidth();
    window.addEventListener('resize', setScrollbarWidth);
  }

  // ページ内アンカー（a[href^="#"]）のスクロール。
  // Lenis.scrollTo は着地 Y をクリック時点で1回だけ確定するため、初期表示時にフォント反映や
  // 画像の遅延ロードでページ高さが伸びると target が下へズレて手前で止まる。
  // そこで Lenis を一時停止し、毎フレーム target の最終位置を取り直す rAF アニメで
  // 「1本の連続モーション」として追従させる（伸びても自然に正しい位置へ収束する）。
  // スクロール連動モジュールは native 'scroll' も購読しているため、停止中も追従して動く。
  smoothScroll() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1);
    const duration = 900;

    // 固定ヘッダー(#gHeader)の高さ分だけ手前で止める。SP/PC で変わるので都度取得。
    const getHeaderOffset = () => {
      const header = document.getElementById('gHeader');
      return header ? header.getBoundingClientRect().height : 0;
    };

    document.querySelectorAll('a[href^="#"]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        const href = trigger.getAttribute('href');
        if (!href || href === '#') return;
        const target = document.getElementById(href.replace('#', ''));
        if (!target) return;

        e.preventDefault();
        e.stopPropagation();

        const offset = getHeaderOffset();

        // reduced-motion は即時ジャンプ
        if (prefersReducedMotion) {
          window.scrollTo(0, window.pageYOffset + target.getBoundingClientRect().top - offset);
          return;
        }

        const lenis = window.__lenis;
        const startY = window.pageYOffset || document.documentElement.scrollTop;
        let startTime = null;
        let rafId = 0;
        let aborted = false;

        // 手動操作（Lenis 停止中の素の wheel/touch）で中断
        const abort = () => { aborted = true; };
        window.addEventListener('wheel', abort, { passive: true, once: true });
        window.addEventListener('touchstart', abort, { passive: true, once: true });

        const finish = () => {
          cancelAnimationFrame(rafId);
          window.removeEventListener('wheel', abort);
          window.removeEventListener('touchstart', abort);
          // Lenis を現在地へ同期して再開（内部位置の巻き戻り防止）
          if (lenis && typeof lenis.start === 'function') {
            lenis.start();
            if (typeof lenis.scrollTo === 'function') {
              lenis.scrollTo(window.scrollY, { immediate: true, force: true });
            }
          }
        };

        // Lenis を一時停止して素の scroll を専有
        if (lenis && typeof lenis.stop === 'function') lenis.stop();

        const step = (now) => {
          if (aborted) { finish(); return; }
          if (startTime === null) startTime = now;
          // 毎フレーム最終位置を取り直す → 高さが伸びても連続モーションで追従
          const targetY = window.pageYOffset + target.getBoundingClientRect().top - offset;
          const t = Math.min(1, (now - startTime) / duration);
          window.scrollTo(0, startY + (targetY - startY) * easeInOutCubic(t));
          if (t < 1) {
            rafId = requestAnimationFrame(step);
          } else {
            // 終了時にもう一度確定位置へ合わせる
            window.scrollTo(0, window.pageYOffset + target.getBoundingClientRect().top - offset);
            finish();
          }
        };
        rafId = requestAnimationFrame(step);
      });
    });
  }

  scrollAnimation() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      const $target = document.querySelectorAll('[data-sai]');
      for (let i = 0; i < $target.length; i++) {
        const targetEach = $target[i];
        targetEach.classList.add('sai-animate');
      }
      return;
    }

    const $target = document.querySelectorAll('[data-sai]');
    for (let i = 0; i < $target.length; i++) {
      const targetEach = $target[i];
      targetEach.classList.add('sai-init');
    }
    const handleLoad = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      for (let i = 0; i < $target.length; i++) {
        const targetEach = $target[i],
          rect = targetEach.getBoundingClientRect(),
          targetPosition = scrollTop + rect.top;
        if (targetPosition < scrollTop) {
          targetEach.classList.add('sai-animate');
        }
      }
    };

    window.addEventListener('load', handleLoad, false);
    // Also handle when lenis is ready
    if (window.__lenis) {
      handleLoad();
    }
    const options = {
      offset: 150,
      delay: 0,
      easing: 'ease',
      duration: 1600,
      disable: false,
      once: false
    };

    document.querySelector('body').setAttribute('data-sai-easing', options.easing);
    document.querySelector('body').setAttribute('data-sai-duration', options.duration);
    document.querySelector('body').setAttribute('data-sai-delay', options.delay);

    const callback = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('sai-animate');
        }
      });
    };

    const option = { rootMargin: -options.offset + 'px 0px' };
    const io = new IntersectionObserver(callback, option);
    const targets = document.querySelectorAll('.sai-init');
    targets.forEach(target => {
      io.observe(target);
    });
  }

  improveAccessibility() {
    document.querySelectorAll('br').forEach(br => {
      br.setAttribute('aria-hidden', 'true');
    });

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            if (node.tagName === 'BR') {
              node.setAttribute('aria-hidden', 'true');
            }
            node.querySelectorAll('br').forEach(br => {
              br.setAttribute('aria-hidden', 'true');
            });
          }
        });
      });
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    const applyAriaLabelToHeadings = () => {
      const getVisibleText = el => {
        let text = '';
        const altTexts = new Set();
        el.childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent;
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const style = window.getComputedStyle(node);
            if (style.display !== 'none' && style.visibility !== 'hidden') {
              if (node.tagName === 'IMG') {
                if (node.alt) {
                  altTexts.add(node.alt);
                }
              } else {
                text += getVisibleText(node);
              }
            }
          }
        });
        altTexts.forEach(alt => {
          text += ' ' + alt;
        });
        return text;
      };

      document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(h => {
        const visibleText = getVisibleText(h)
          .replace(/\s+/g, ' ')
          .trim();
        h.setAttribute('aria-label', visibleText);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', applyAriaLabelToHeadings);
    } else {
      applyAriaLabelToHeadings();
    }

    const headingObserver = new MutationObserver(muts => {
      if (muts.some(m => m.addedNodes.length)) applyAriaLabelToHeadings();
    });
    headingObserver.observe(document.body, { childList: true, subtree: true });
  }

  initPrint() {
    const $print = document.querySelectorAll('.js-print');
    if (!$print.length) return;
    $print.forEach((btn) => {
      btn.addEventListener('click', () => window.print());
    });
  }
}
